import type { RichTextPanelItem } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import cn from 'classnames'
import * as R from 'ramda'
import { useCallback, useEffect } from 'react'
import React from 'react'

import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import ScrollService, { scrollProps } from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getActiveElementId, getElementState } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'

import * as s from './AccordionFormValueItem.module.scss'
import type { AccordionState } from './accordionTypes'

export const AccordionFormValueItem: React.FC<IRenderItemProps<RichTextPanelItem>> = ({
  index,
  item,
  drag,
  onChange,
  value: array,
  min,
  max,
  isDragging,
}) => {
  const dispatch = useProjectDispatch()
  const elementId = useProjectContext(getActiveElementId)
  const state = useProjectContext(getElementState, elementId || '')
  const isActive = item.value === state?.active
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)

  const setState = useCallback(
    (value: AccordionState) => {
      if (elementId) {
        dispatch(setElement({ id: elementId, value }))
      }
    },
    [dispatch, elementId],
  )

  const handleBlur = useCallback(() => setState({ ...state, active: null }), [setState, state])

  const handleFocus = useCallback(
    () => setState({ ...state, active: item.value }),
    [setState, state, item.value],
  )

  const onClone = useCallback(() => {
    const clone = { ...R.clone(item), value: generateId() }
    return onChange?.([...array, clone], { command: 'clone', item, clone })
  }, [onChange, array, item])

  const onDelete = useCallback(() => {
    return onChange?.(
      array.filter(({ value }) => value !== item.value),
      { command: 'delete', item },
    )
  }, [onChange, array, item])

  useEffect(() => {
    if (isActive) {
      ScrollService.scroll({ container: ScrollContainerEnum.toolbar, id: item.value }, null)
    }
  }, [isActive, item.value])

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      {...scrollProps(item.value)}
      className={cn(s.root, { [s.active]: !isDragging && state?.active === item.value })}
      onBlur={handleBlur}
      onMouseDown={handleFocus}
      ref={drag}
    >
      <div className={s.title}>
        <Icon name='builderDrugAndDrop' size={KitSize.S} />
        <Typography>
          {t('elements.accordion.form.component')} {index + 1}
        </Typography>
      </div>
      <div className={s.actions}>
        <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
          <IconButton
            disabled={disableClone}
            icon='otherCopy'
            name='clone'
            onClick={onClone}
            size={KitSize.XS}
            styleType='ghost'
          />
        </Tooltip>
        <Tooltip overlay={t('uiKit.tooltip.delete')}>
          <IconButton
            disabled={disableDelete}
            icon='otherTrash'
            name='delete'
            onClick={onDelete}
            size={KitSize.XS}
            styleType='ghost'
          />
        </Tooltip>
      </div>
    </div>
  )
}

export default React.memo(AccordionFormValueItem) as typeof AccordionFormValueItem
